// Mobile parity: "How did you hear about us?" radio list.
// Also auto-saves firstName/lastName/name from auth.displayName or parsed résumé
// so there's no dedicated name screen.
import { useEffect, useState } from "react"
import { auth } from "../../firebaseConfig"
import { useOnboarding } from "../../contexts/OnboardingContext"
import {
  OnboardingStepShell,
  ContinueButton,
  RadioOption,
  OB_COLORS as C,
} from "./OnboardingStepShell"

const OPTIONS = [
  { id: "friend_referral", label: "Friend / Referral" },
  { id: "y_combinator",    label: "Y Combinator" },
  { id: "tiktok",          label: "TikTok" },
  { id: "instagram",       label: "Instagram" },
  { id: "linkedin",        label: "LinkedIn" },
  { id: "google_search",   label: "Google Search" },
  { id: "university",      label: "University / Career Center" },
  { id: "other",           label: "Other" },
]

function Leading({ id }) {
  const sz = 20
  const common = { width: sz, height: sz }
  const stroke = "#52525b"
  if (id === "y_combinator") {
    return (
      <div style={{
        width: 22, height: 22, borderRadius: 5,
        background: "#F26522", color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 13, fontWeight: 900,
      }}>Y</div>
    )
  }
  if (id === "linkedin") {
    return (
      <div style={{
        width: 22, height: 22, borderRadius: 4,
        background: "#0A66C2", color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 11, fontWeight: 800,
      }}>in</div>
    )
  }
  if (id === "friend_referral") return (
    <svg {...common} viewBox="0 0 20 20" fill="none">
      <circle cx="7" cy="8" r="3" stroke={stroke} strokeWidth="1.4" />
      <circle cx="14" cy="9" r="2.4" stroke={stroke} strokeWidth="1.4" />
      <path d="M2 17c0-2.5 2.2-4.5 5-4.5s5 2 5 4.5" stroke={stroke} strokeWidth="1.4" strokeLinecap="round" />
      <path d="M12 17c0-1.8 1.4-3.2 3.2-3.2 1.5 0 2.8 1 3.1 2.3" stroke={stroke} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  )
  if (id === "tiktok") return (
    <svg {...common} viewBox="0 0 20 20" fill="none">
      <path d="M12 3v9.5a3 3 0 11-3-3M12 3c0 2 1.6 3.6 3.6 3.6" stroke={stroke} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
  if (id === "instagram") return (
    <svg {...common} viewBox="0 0 20 20" fill="none">
      <rect x="3" y="3" width="14" height="14" rx="4" stroke={stroke} strokeWidth="1.4" />
      <circle cx="10" cy="10" r="3" stroke={stroke} strokeWidth="1.4" />
      <circle cx="14.2" cy="5.8" r="0.6" fill={stroke} />
    </svg>
  )
  if (id === "google_search") return (
    <svg {...common} viewBox="0 0 20 20" fill="none">
      <circle cx="9" cy="9" r="5" stroke={stroke} strokeWidth="1.4" />
      <path d="M13 13l4 4" stroke={stroke} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  )
  if (id === "university") return (
    <svg {...common} viewBox="0 0 20 20" fill="none">
      <path d="M2 7l8-3.5L18 7l-8 3.5L2 7z" stroke={stroke} strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M5 9v4.5c0 1.4 2.2 2.5 5 2.5s5-1.1 5-2.5V9" stroke={stroke} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  )
  // other
  return (
    <svg {...common} viewBox="0 0 20 20" fill="none">
      <circle cx="5" cy="10" r="1.4" fill={stroke} />
      <circle cx="10" cy="10" r="1.4" fill={stroke} />
      <circle cx="15" cy="10" r="1.4" fill={stroke} />
    </svg>
  )
}

export default function HowHeardStep({ onContinue, onBack, step, totalSteps }) {
  const { profile, updateField } = useOnboarding()
  const [selected, setSelected] = useState(profile?.howHeard || null)

  // Auto-save first/last/name from auth displayName or résumé parse — no screen.
  useEffect(() => {
    const user   = auth.currentUser
    const authNm = (user?.displayName || "").trim()
    const resNm  = (profile?.name || "").trim()
    const chosen = authNm || resNm
    if (!chosen) return
    const parts = chosen.split(/\s+/)
    const firstName = parts[0] || ""
    const lastName  = parts.length > 1 ? parts.slice(1).join(" ") : ""
    if (firstName) {
      updateField({ firstName, lastName, name: `${firstName} ${lastName}`.trim() })
    }
  }, [])

  const pick = (id) => {
    setSelected(id)
    updateField({ howHeard: id })
  }

  return (
    <OnboardingStepShell
      title={"How did you\nhear about us?"}
      step={step}
      totalSteps={totalSteps}
      onBack={onBack}
      bottom={<ContinueButton label="Continue" onClick={onContinue} disabled={!selected} />}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {OPTIONS.map((o) => (
          <RadioOption
            key={o.id}
            label={o.label}
            leading={<Leading id={o.id} />}
            selected={selected === o.id}
            onClick={() => pick(o.id)}
          />
        ))}
      </div>
    </OnboardingStepShell>
  )
}
